import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

/**
 * StickyHeader
 *
 * Manages a sticky header's visibility state based on scroll direction.
 * Pure CSS class toggling — no animations. Extend with AnimatedStickyHeader for GSAP motion.
 *
 * Features:
 * - State machine: TOP → FIXED → HIDDEN
 * - CSS class toggling (optional via `useClasses`)
 * - Named thresholds at arbitrary scroll positions (dynamic add/remove)
 * - Event callbacks via `.on()`
 * - Uses GSAP ScrollTrigger as scroll observer (no animation dependency)
 *
 * States:
 * - TOP:    User is at the top of the page. Header in natural position.
 * - FIXED:  Header is visible/fixed (user scrolling up or at bottom).
 * - HIDDEN: Header is hidden (user scrolling down past offset).
 *
 * CSS classes applied:
 * - .sticky-header                          — always (base)
 * - .sticky-header--fixed                   — state is FIXED
 * - .sticky-header--hidden                  — state is HIDDEN
 * - .sticky-header--not-top                 — scrollY > 0
 * - .sticky-header--past-offset             — scrollY > offset
 * - .sticky-header--threshold-{name}        — past a named threshold
 *
 * @example
 * const header = new StickyHeader({
 *     element: document.querySelector('.site-header'),
 *     offset: 100,
 *     thresholds: { compact: 150, hero: 600 },
 *     on: {
 *         stateChange: (newState, oldState, ctx) => { },
 *         'state:hidden': (ctx) => { },
 *         'state:fixed': (ctx) => { },
 *         'threshold:compact': ({ crossed }) => { },
 *     },
 * })
 *
 * header.on('state:hidden', (ctx) => console.log('Header hidden'))
 * header.addThreshold('cta', 1200, ({ crossed }) => { })
 * header.destroy()
 */
export class StickyHeader {
	static STATES = Object.freeze({
		TOP: 'top',
		FIXED: 'fixed',
		HIDDEN: 'hidden',
	})

	static DEFAULTS = Object.freeze({
		element: null,
		selector: 'header',
		offset: 50,
		tolerance: { up: 2, down: 2 },
		showAtBottom: true,
		useClasses: true,
		classNames: {
			base: 'sticky-header',
			fixed: 'sticky-header--fixed',
			hidden: 'sticky-header--hidden',
			notTop: 'sticky-header--not-top',
			pastOffset: 'sticky-header--past-offset',
		},
		thresholds: {},
		on: {},
	})

	constructor(options = {}) {
		this.options = this._mergeOptions(StickyHeader.DEFAULTS, options)

		this.el = this.options.element || document.querySelector(this.options.selector)

		if (!this.el) {
			console.warn('StickyHeader: No header element found.')
			return
		}

		// Internal state
		this._state = StickyHeader.STATES.TOP
		this._direction = null
		this._lastScrollY = 0
		this._activeThresholds = new Set()
		this._listeners = {} // event → [callback, ...]
		this._scrollTrigger = null
		this._destroyed = false

		// Register callbacks from options.on
		if (this.options.on) {
			for (const [event, callback] of Object.entries(this.options.on)) {
				this.on(event, callback)
			}
		}

		this._init()
	}

	// ──────────────────────────────────────────────
	// Lifecycle
	// ──────────────────────────────────────────────

	_init() {
		this._frozen = false

		this._lastScrollY = window.scrollY

		if (this.options.useClasses) {
			this.el.classList.add(this.options.classNames.base)
		}

		this._scrollTrigger = ScrollTrigger.create({
			start: 0,
			end: 'max',
			onUpdate: (self) => {
				// console.log('ok')
				this._onScroll(self)
			},
		})
		// console.log(this._scrollTrigger)

		// Evaluate initial state
		this._onScroll()

		this._emit('init', this._createContext())
	}

	destroy() {
		if (this._destroyed) return
		this._destroyed = true

		// Kill ScrollTrigger
		if (this._scrollTrigger) {
			this._scrollTrigger.kill(true)
			this._scrollTrigger = null
		}

		// Remove all classes
		if (this.options.useClasses) {
			const cn = this.options.classNames
			this.el.classList.remove(cn.base, cn.fixed, cn.hidden, cn.notTop, cn.pastOffset)

			for (const name of Object.keys(this.options.thresholds)) {
				this.el.classList.remove(this._getThresholdClass(name))
			}
		}

		this._emit('destroy', this._createContext())

		// Clear all listeners
		this._listeners = {}
	}

	/**
	 * Force recalculation of all states and thresholds.
	 * Useful after DOM changes that affect scroll height.
	 */
	refresh() {
		if (this._scrollTrigger) {
			this._scrollTrigger.refresh()
		}
		this._onScroll()
	}

	/**
	 * Pause scroll reactions. Header stays in current state.
	 */
	freeze() {
		this._frozen = true
		return this
	}

	/**
	 * Resume scroll reactions.
	 */
	unfreeze() {
		this._frozen = false
		this._lastScrollY = window.scrollY // prevent jump
		return this
	}

	get isFrozen() {
		return this._frozen
	}

	// ──────────────────────────────────────────────
	// Scroll handling
	// ──────────────────────────────────────────────

	_onScroll() {
		if (this._destroyed || this._frozen) return

		const scrollY = window.scrollY
		const lastScrollY = this._lastScrollY
		const delta = scrollY - lastScrollY
		const absDelta = Math.abs(delta)

		// Direction
		const newDirection = delta > 0 ? 'down' : delta < 0 ? 'up' : this._direction
		if (newDirection !== this._direction) {
			this._direction = newDirection
			this._emit('directionChange', newDirection, this._createContext())
		}

		// CSS modifier classes
		this._updateModifierClasses(scrollY)

		// Thresholds
		this._evaluateThresholds(scrollY)

		// Resolve tolerance per direction
		const tolerance = this._getTolerance(newDirection)
		const toleranceReached = absDelta >= tolerance

		if (scrollY <= 0) {
			this._setState(StickyHeader.STATES.TOP)
		} else if (this.options.showAtBottom && this._isAtBottom(scrollY)) {
			this._setState(StickyHeader.STATES.FIXED)
		} else if (scrollY > this.options.offset && toleranceReached) {
			if (newDirection === 'down') {
				this._setState(StickyHeader.STATES.HIDDEN)
			} else if (newDirection === 'up') {
				this._setState(StickyHeader.STATES.FIXED)
			}
		}

		// General scroll event
		this._emit('scroll', this._createContext())

		this._lastScrollY = scrollY
	}

	/**
	 * Resolve tolerance value for current direction.
	 * Accepts a number or { up: number, down: number }.
	 */
	_getTolerance(direction) {
		const t = this.options.tolerance
		if (typeof t === 'number') return t
		return direction === 'up' ? (t.up ?? 0) : (t.down ?? 0)
	}

	_isAtBottom(scrollY) {
		// console.log('Checking if at bottom:', {
		// 	scrollY,
		// 	innerHeight: window.innerHeight,
		// 	scrollHeight: document.documentElement.scrollHeight,
		// })
		return window.innerHeight + scrollY >= document.documentElement.scrollHeight - 2
	}

	// ──────────────────────────────────────────────
	// State machine
	// ──────────────────────────────────────────────

	_setState(newState) {
		if (newState === this._state) return

		const oldState = this._state
		this._state = newState

		this._applyState(newState, oldState)

		const ctx = this._createContext()

		// General state change event
		this._emit('stateChange', newState, oldState, ctx)

		// Namespaced state event
		this._emit(`state:${newState}`, ctx)
	}

	/**
	 * Apply state changes to the DOM.
	 * Override this in subclasses to add animation.
	 */
	_applyState(newState, oldState) {
		if (!this.options.useClasses) return

		const cn = this.options.classNames

		switch (newState) {
			case StickyHeader.STATES.TOP:
				this.el.classList.remove(cn.fixed, cn.hidden)
				break

			case StickyHeader.STATES.FIXED:
				this.el.classList.add(cn.fixed)
				this.el.classList.remove(cn.hidden)
				break

			case StickyHeader.STATES.HIDDEN:
				this.el.classList.add(cn.hidden)
				// Keep cn.fixed — element needs position:fixed while
				// CSS transitions it out of view
				break
		}
	}

	// ──────────────────────────────────────────────
	// Modifier classes (not-top, past-offset)
	// ──────────────────────────────────────────────

	_updateModifierClasses(scrollY) {
		if (!this.options.useClasses) return

		const cn = this.options.classNames
		this.el.classList.toggle(cn.notTop, scrollY > 0)
		this.el.classList.toggle(cn.pastOffset, scrollY > this.options.offset)
	}

	// ──────────────────────────────────────────────
	// Thresholds
	// ──────────────────────────────────────────────

	_evaluateThresholds(scrollY) {
		for (const [name, value] of Object.entries(this.options.thresholds)) {
			const wasPast = this._activeThresholds.has(name)
			const isPast = scrollY > value

			if (isPast === wasPast) continue

			if (isPast) {
				this._activeThresholds.add(name)
				if (this.options.useClasses) {
					this.el.classList.add(this._getThresholdClass(name))
				}
			} else {
				this._activeThresholds.delete(name)
				if (this.options.useClasses) {
					this.el.classList.remove(this._getThresholdClass(name))
				}
			}

			this._emit(`threshold:${name}`, {
				name,
				crossed: isPast,
				direction: isPast ? 'down' : 'up',
				scrollY,
			})
		}
	}

	_getThresholdClass(name) {
		return `${this.options.classNames.base}--threshold-${name}`
	}

	/**
	 * Add a named threshold at runtime.
	 * @param {string}   name     — unique name
	 * @param {number}   value    — scroll position in px
	 * @param {function} [callback] — optional callback when crossed
	 * @returns {this}
	 */
	addThreshold(name, value, callback) {
		this.options.thresholds[name] = value
		if (callback) {
			this.on(`threshold:${name}`, callback)
		}
		this._evaluateThresholds(window.scrollY)
		return this
	}

	/**
	 * Remove a named threshold at runtime.
	 * @param {string} name
	 * @returns {this}
	 */
	removeThreshold(name) {
		delete this.options.thresholds[name]
		this._activeThresholds.delete(name)
		if (this.options.useClasses) {
			this.el.classList.remove(this._getThresholdClass(name))
		}
		return this
	}

	/**
	 * Check if a named threshold is currently active.
	 * @param {string} name
	 * @returns {boolean}
	 */
	isThresholdActive(name) {
		return this._activeThresholds.has(name)
	}

	// ──────────────────────────────────────────────
	// Event system
	// ──────────────────────────────────────────────

	/**
	 * Events:
	 *   init, destroy, scroll, stateChange, directionChange,
	 *   state:top, state:fixed, state:hidden,
	 *   threshold:{name}
	 *
	 * @param {string}   event
	 * @param {function} callback
	 * @returns {this}
	 */
	on(event, callback) {
		if (typeof callback !== 'function') return this

		if (!this._listeners[event]) {
			this._listeners[event] = []
		}
		this._listeners[event].push(callback)
		return this
	}

	/**
	 * Remove an event callback. If no callback specified, removes all for that event.
	 * @param {string}   event
	 * @param {function} [callback]
	 * @returns {this}
	 */
	off(event, callback) {
		if (!this._listeners[event]) return this

		if (!callback) {
			delete this._listeners[event]
		} else {
			this._listeners[event] = this._listeners[event].filter((fn) => fn !== callback)
		}
		return this
	}

	_emit(event, ...args) {
		const handlers = this._listeners[event]
		if (!handlers || handlers.length === 0) return

		for (const fn of handlers) {
			fn(...args)
		}
	}

	// ──────────────────────────────────────────────
	// Context & getters
	// ──────────────────────────────────────────────

	_createContext() {
		return {
			instance: this,
			state: this._state,
			direction: this._direction,
			scrollY: window.scrollY,
			activeThresholds: [...this._activeThresholds],
		}
	}

	get state() {
		return this._state
	}

	get direction() {
		return this._direction
	}

	get isTop() {
		return this._state === StickyHeader.STATES.TOP
	}

	get isFixed() {
		return this._state === StickyHeader.STATES.FIXED
	}

	get isHidden() {
		return this._state === StickyHeader.STATES.HIDDEN
	}

	// ──────────────────────────────────────────────
	// Utilities
	// ──────────────────────────────────────────────

	/**
	 * Deep merge options, handling nested classNames object.
	 */
	_mergeOptions(defaults, overrides) {
		const merged = { ...defaults, ...overrides }

		// Deep merge classNames
		merged.classNames = {
			...defaults.classNames,
			...(overrides.classNames || {}),
		}

		// Don't carry over the `on` from defaults into merged —
		// we handle listener registration separately
		merged.on = overrides.on || {}

		// Shallow copy thresholds so we don't mutate defaults
		merged.thresholds = { ...(overrides.thresholds || {}) }

		return merged
	}
}
